"use client";
import { ark } from "@ark-ui/react/factory";
import { forwardRef, ReactNode } from "react";
import { cva, cx } from "styled-system/css";

import { focusRing } from "@/recipes";

import { ButtonProps, buttonStyles, ButtonVariants } from "./button";
import { ButtonContentWithSpinner } from "./shared";

export interface IconButtonProps extends Omit<ButtonProps, "fullWidth" | "LeadIcon" | "TailIcon"> {
  /** The icon to render inside the button. This will be replaced with a spinner when `loading` is set to true. */
  children: ReactNode;
  /** The aria-label of the button. This is required because an icon does not contain text to label the button. */
  ariaLabel: string;
  /** The style variant of the button */
  variant?: Exclude<ButtonVariants["variant"], "outline">;
}

/** A `Button` for displaying a single icon without a visible label. */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      variant = "primary",
      size = "md",
      loading,
      disabled,
      ariaLabel,
      children,
      render,
      className,
      ...props
    },
    ref,
  ) => {
    const childrenToRender = (
      <ButtonContentWithSpinner loading={loading} size={size} variant={variant}>
        {children}
      </ButtonContentWithSpinner>
    );

    return (
      <ark.button
        ref={ref}
        data-loading={loading || undefined}
        disabled={disabled || loading}
        aria-label={ariaLabel}
        className={cx(
          buttonStyles({ variant, size }),
          buttonOverrideStyles({ size }),
          focusRing({ rounded: "350" }),
          className,
        )}
        asChild={!!render}
        {...props}
      >
        {render ? render(childrenToRender) : childrenToRender}
      </ark.button>
    );
  },
);
IconButton.displayName = "IconButton";

const buttonOverrideStyles = cva({
  base: {},
  variants: {
    size: {
      // Since our Figma uses inner borders and CSS doesn't natively support that,
      // we're subtracting the border width from the padding
      sm: {
        "& svg": {
          h: "16",
          w: "16",
          transform: "scale(1)",
          transitionPreset: "transform",
        },
        "&:hover:not(:disabled), &:active": { "& svg": { transform: "scale(1.0625)" } },
        p: "[calc({spacing.4} - {borderWidths.100})]",
      },
      md: {
        "& svg": {
          h: "16",
          w: "16",
          transform: "scale(1)",
          transitionPreset: "transform",
        },
        "&:hover:not(:disabled), &:active": { "& svg": { transform: "scale(1.125)" } },
        p: "[calc({spacing.8} - {borderWidths.100})]",
      },
      lg: {
        "& svg": {
          h: "20",
          w: "20",
          transform: "scale(1)",
          transitionPreset: "transform",
        },
        "&:hover:not(:disabled), &:active": { "& svg": { transform: "scale(1.1)" } },
        p: "[calc({spacing.12} - {borderWidths.100})]",
      },
    },
  },
});
